import {
  GetKycP2PRequest,
  GetKycP2PResponse,
} from "@services/models/Kyc/GetKycP2PModel";
import {
  AdminReverseEscrowRequest,
  AdminReverseEscrowResponse,
} from "@services/models/Prime/AdminReverseRequest";
import {
  AdminSettleEscrowRequest,
  AdminSettleEscrowResponse,
} from "@services/models/Prime/AdminSettleRequest";
import {
  AssignAgentToTradeRequest,
  AssignAgentToTradeResponse,
} from "@services/models/Prime/AssignAgentToTradeRequest";
import {
  ExtendNtxRampTradeDeadlinesResponse,
  GetBrPixStatusResponse,
  UpdateBrPixStatusRequest,
  UpdateBrPixStatusResponse,
} from "@services/models/Prime/BrPixStatusModel";
import {
  GetUpiStatusResponse,
  UpdateUpiStatusRequest,
  UpdateUpiStatusResponse,
} from "@services/models/Prime/UpiStatusModel";
import {
  ChatHistoryRequest,
  ChatHistoryResponse,
} from "@services/models/Prime/ChatHistoryModel";
import {
  GetCadInstancesRequest,
  GetCadInstancesResponse,
} from "@services/models/Prime/GetCadInstancesModel";
import {
  CadInternalTransferRequest,
  CadInternalTransferResponse,
} from "@services/models/Prime/CadInternalTransferModel";
import {
  CadManualPayoutRequest,
  CadManualPayoutResponse,
} from "@services/models/Prime/CadManualPayoutModel";
import {
  OpsClearRequest,
  OpsClearResponse,
} from "@services/models/Prime/OpsClearModel";
import {
  CadIvyActionResponse,
  ReinitiateRequest,
} from "@services/models/Prime/CadIvyRefundReinitiateModel";
import {
  GetDisputedTradesListRequest,
  GetDisputedTradesListResponse,
} from "@services/models/Prime/GetDisputedTradeListModel";
import {
  GetIvyInstancesRequest,
  GetIvyInstancesResponse,
} from "@services/models/Prime/GetIvyInstancesModel";
import {
  GetPHPInstancesRequest,
  GetPHPInstancesResponse,
} from "@services/models/Prime/GetPHPInstancesModel";
import {
  GetPixTransactionRequest,
  PixTransactionData,
} from "@services/models/Prime/GetPixTransactionModel";
import {
  GetPrimeCadTradeDetailsRequest,
  GetPrimeCadTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeCadTradeDetailsModel";
import {
  GetPrimeEuropeTradeDetailsRequest,
  GetPrimeEuropeTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeEuropeTradeDetailsModel";
import {
  GetPrimePHPTradeDetailsRequest,
  GetPrimePHPTradeDetailsResponse,
} from "@services/models/Prime/GetPrimePHPTradeDetailsModel";
import {
  GetPrimeTradeDetailsRequest,
  GetPrimeTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeTradeDetailsModel";
import {
  GetPrimeTradesListRequest,
  GetPrimeTradesListResponse,
} from "@services/models/Prime/GetPrimeTradeListModel";
import { GetRampRatesResponse } from "@services/models/Prime/GetRampRatesModel";
import {
  GetTradeCallBackLogsRequest,
  GetTradeCallBackLogsResponse,
} from "@services/models/Prime/GetTradeCallBackLogsModel";
import {
  SendCallbackForLogRequest,
  SendCallbackForLogResponse,
} from "@services/models/Prime/SendCallbackForLogModel";
import {
  PrimeIndiaVpnConfigResponse,
  UpdatePrimeIndiaVpnConfigRequest,
} from "@services/models/Prime/PrimeIndiaVpnConfigModel";
import {
  SendMessageRequest,
  SendMessageResponse,
} from "@services/models/Prime/SendMessageModel";
import {
  ExpireNtxRampTradesRequest,
  ExpireNtxRampTradesResponse,
} from "@services/models/Prime/ExpireNtxRampTradesModel";
import { GetNtxBalanceResponse } from "@services/models/Prime/NtxBalanceModel";
import {
  GetCircuitBreakerConfigResponse,
  UpdateCircuitBreakerConfigRequest,
  UpdateCircuitBreakerConfigResponse,
} from "@services/models/Prime/CircuitBreakerConfigModel";
import {
  GetNtxBrlRatesResponse,
  SetNtxBrlSpreadsRequest,
  SetNtxBrlSpreadsResponse,
} from "@services/models/Prime/NtxBrlRatesModel";
import { NtxManualSettlementResponse } from "@services/models/Prime/NtxManualSettlementModel";
import {
  SetRatesRequest,
  SetRatesResponse,
} from "@services/models/Prime/SetRatesModel";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

class PrimeApiService {
  private api: AxiosInstance;
  private silentApi: AxiosInstance;
  constructor() {
    this.api = createApiClient();
    this.silentApi = createApiClient({ notifyErrors: false });
  }

  public async getPrimeTradesList(
    data: GetPrimeTradesListRequest,
  ): Promise<GetPrimeTradesListResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getPrimeTradesList`, {
        params: {
          tradeType: data.tradeType,
          tradeStatus: data.tradeStatus,
          page: data.page,
          rows: data.rows,
          primeType: data.primeType ? data.primeType : undefined,
          isAutoSettle: data.isAutoSettle ? data.isAutoSettle : undefined,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getIvyInstancesList(
    data: GetIvyInstancesRequest,
  ): Promise<GetIvyInstancesResponse> {
    try {
      const response = await this.api.get("/adminPanel/getIvyInstancesList", {
        params: {
          type: data.type,
          page: data.page,
          rows: data.rows,
          searchTerm: data.searchTerm ? data.searchTerm : undefined,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async searchPrimeTrade(
    searchTerm: string,
    primeType?: "IN" | "BR",
  ): Promise<GetPrimeTradesListResponse> {
    try {
      const response = await this.api.get(`/adminPanel/searchPrimeTrade`, {
        params: {
          searchTerm: searchTerm,
          primeType: primeType,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getDisputedTradesList(
    data: GetDisputedTradesListRequest,
  ): Promise<GetDisputedTradesListResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getDisputedTradesList`, {
        params: {
          tradeType: data.tradeType,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPrimeTradeDetails(
    data: GetPrimeTradeDetailsRequest,
  ): Promise<GetPrimeTradeDetailsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getPrimeTradeDetails`, {
        params: {
          tradeId: data.tradeId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPixTransaction(
    data: GetPixTransactionRequest,
  ): Promise<PixTransactionData> {
    try {
      const response = await this.api.get<PixTransactionData>(
        `/adminPanel/ntxPixTransaction/${data.id}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getIvyInstanceDetails(
    data: GetPrimeEuropeTradeDetailsRequest,
  ): Promise<GetPrimeEuropeTradeDetailsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getIvyInstanceDetails`, {
        params: {
          instanceId: data.instanceId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getTradeCallbackLogs(
    data: GetTradeCallBackLogsRequest,
    endPoint?: string,
  ): Promise<GetTradeCallBackLogsResponse> {
    try {
      const response = await this.api.get(
        `${endPoint ? endPoint : "/adminPanel/getTradeCallbackLogs"}`,
        {
          params: {
            tradeId: data.tradeId,
            instanceId: data.instanceId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async sendCallbackForLog(
    data: SendCallbackForLogRequest,
    endPoint?: string,
  ): Promise<SendCallbackForLogResponse> {
    try {
      const response = await this.api.post(
        `${endPoint ? endPoint : "/adminPanel/sendPrimeCallbackForLog"}`,
        data,
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPHPInstancesList(
    data: GetPHPInstancesRequest,
  ): Promise<GetPHPInstancesResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getPHPInstancesList`, {
        params: {
          type: data.type,
          page: data.page,
          rows: data.rows,
          searchTerm: data.searchTerm ? data.searchTerm : undefined,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPHPInstanceDetails(
    data: GetPrimePHPTradeDetailsRequest,
  ): Promise<GetPrimePHPTradeDetailsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getPHPInstanceDetails`, {
        params: {
          instanceId: data.instanceId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPHPCallbackLogs(
    data: GetTradeCallBackLogsRequest,
  ): Promise<GetTradeCallBackLogsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getPHPCallbackLogs`, {
        params: {
          tradeId: data.tradeId,
          instanceId: data.instanceId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCadInstancesList(
    data: GetCadInstancesRequest,
  ): Promise<GetCadInstancesResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getCadInstancesList`, {
        params: {
          type: data.type,
          page: data.page,
          rows: data.rows,
          searchTerm: data.searchTerm ? data.searchTerm : undefined,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCadInstanceDetails(
    data: GetPrimeCadTradeDetailsRequest,
  ): Promise<GetPrimeCadTradeDetailsResponse> {
    try {
      const response = await this.api.get(`/adminPanel/getCadInstanceDetails`, {
        params: {
          instanceId: data.instanceId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async adminSettleEscrow(
    data: AdminSettleEscrowRequest,
  ): Promise<AdminSettleEscrowResponse> {
    try {
      const response = await this.api.post<AdminSettleEscrowResponse>(
        `/adminPanel/prime/trade/settle/${data.tradeId}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async adminReverseEscrow(
    data: AdminReverseEscrowRequest,
  ): Promise<AdminReverseEscrowResponse> {
    try {
      const response = await this.api.post<AdminReverseEscrowResponse>(
        `/adminPanel/prime/trade/reverse/${data.tradeId}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async assignAgentToTrade(
    data: AssignAgentToTradeRequest,
  ): Promise<AssignAgentToTradeResponse> {
    try {
      const response = await this.api.get<AssignAgentToTradeResponse>(
        `/adminPanel/assignAgentToRampTrade`,
        {
          params: { tradeId: data.tradeId },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async setRates(data: SetRatesRequest): Promise<SetRatesResponse> {
    try {
      const response = await this.api.post<SetRatesResponse>(
        "/adminPanel/setRampRates",
        {
          buyRate: data.buyRate,
          sellRate: data.sellRate,
          p2pCryptoCurrencyId: data.p2pCryptoCurrencyId,
          fiatCurrencyId: data.fiatCurrencyId,
          merchantId: data.merchantId ?? undefined,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getChatHistoryByTradeId(
    data: ChatHistoryRequest,
  ): Promise<ChatHistoryResponse> {
    try {
      const response = await this.api.get<ChatHistoryResponse>(
        `/adminPanel/getChatHistoryByTradeId`,
        {
          params: {
            tradeId: data.tradeId,
            limit: data.limit,
            offset: data.offset,
            isAdmin: data.isAdmin,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getRampRates(): Promise<GetRampRatesResponse> {
    try {
      const response = await this.api.get<GetRampRatesResponse>(
        "/adminPanel/getRampRates",
        {
          params: {
            rows: 100,
            page: 1,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async expireTrade(tradeId: number): Promise<void> {
    try {
      await this.api.post(`/adminPanel/prime/trade/pix/expire`, {
        tradeId: tradeId,
      });
    } catch (error) {
      throw error;
    }
  }

  public async sendMessage(
    data: SendMessageRequest,
  ): Promise<SendMessageResponse> {
    try {
      const response = await this.api.post<SendMessageResponse>(
        `/adminPanel/sendMessage`,
        {
          tradeId: data.tradeId,
          messageType: data.messageType,
          messageContent: data.messageContent,
          isAdmin: data.isAdmin,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getBrPixStatus(): Promise<GetBrPixStatusResponse> {
    const response = await this.api.get<GetBrPixStatusResponse>(
      "/adminPanel/br/pix/status",
    );
    return response.data;
  }

  public async updateBrPixStatus(
    data: UpdateBrPixStatusRequest,
  ): Promise<UpdateBrPixStatusResponse> {
    const response = await this.api.post<UpdateBrPixStatusResponse>(
      "/adminPanel/br/pix/status",
      data,
    );
    return response.data;
  }

  public async getUpiStatus(): Promise<GetUpiStatusResponse> {
    const response = await this.api.get<GetUpiStatusResponse>(
      "/adminPanel/upi/status",
    );
    return response.data;
  }

  public async updateUpiStatus(
    data: UpdateUpiStatusRequest,
  ): Promise<UpdateUpiStatusResponse> {
    const response = await this.api.post<UpdateUpiStatusResponse>(
      "/adminPanel/upi/status",
      data,
    );
    return response.data;
  }

  public async getNtxBalance(options?: {
    silent?: boolean;
  }): Promise<GetNtxBalanceResponse> {
    const client = options?.silent ? this.silentApi : this.api;
    const response = await client.get<GetNtxBalanceResponse>(
      "/adminPanel/getNtxBalance",
    );
    return response.data;
  }

  public async getCircuitBreakerConfig(options?: {
    silent?: boolean;
  }): Promise<GetCircuitBreakerConfigResponse> {
    const client = options?.silent ? this.silentApi : this.api;
    const response = await client.get<GetCircuitBreakerConfigResponse>(
      "/adminPanel/circuitBreaker/config",
    );
    return response.data;
  }

  public async updateCircuitBreakerConfig(
    data: UpdateCircuitBreakerConfigRequest,
  ): Promise<UpdateCircuitBreakerConfigResponse> {
    const response = await this.api.put<UpdateCircuitBreakerConfigResponse>(
      "/adminPanel/circuitBreaker/config",
      data,
    );
    return response.data;
  }

  public async getNtxBrlRates(): Promise<GetNtxBrlRatesResponse> {
    const response = await this.api.get<GetNtxBrlRatesResponse>(
      "/adminPanel/ntx/brl/rates",
    );
    return response.data;
  }

  public async setNtxBrlSpreads(
    data: SetNtxBrlSpreadsRequest,
  ): Promise<SetNtxBrlSpreadsResponse> {
    const response = await this.api.put<SetNtxBrlSpreadsResponse>(
      "/adminPanel/ntx/brl/spreads",
      data,
    );
    return response.data;
  }

  public async extendNtxRampTradeDeadlines(
    minutes: number,
  ): Promise<ExtendNtxRampTradeDeadlinesResponse> {
    const response = await this.api.post<ExtendNtxRampTradeDeadlinesResponse>(
      "/adminPanel/extendNtxRampTradeDeadlines",
      {
        minutes,
      },
    );
    return response.data;
  }

  public async expireNtxRampTrades(
    data: ExpireNtxRampTradesRequest,
  ): Promise<ExpireNtxRampTradesResponse> {
    const response = await this.api.post<ExpireNtxRampTradesResponse>(
      "/adminPanel/expireNtxRampTrades",
      data,
    );
    return response.data;
  }

  public async manualSettleNtxTrade(
    tradeId: string | number,
  ): Promise<NtxManualSettlementResponse> {
    const response = await this.api.post<NtxManualSettlementResponse>(
      `/adminPanel/ntx/trade/${tradeId}/manualSettlement`,
      {},
    );
    return response.data;
  }

  public async getCustomerServiceP2Pkyc(
    data: GetKycP2PRequest,
  ): Promise<GetKycP2PResponse> {
    try {
      const response = await this.api.get<GetKycP2PResponse>(
        `/adminPanel/getP2Pkyc`,
        {
          params: {
            tradeId: data.tradeId,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPrimeIndiaVpnConfig(): Promise<PrimeIndiaVpnConfigResponse> {
    try {
      const response = await this.api.get<PrimeIndiaVpnConfigResponse>(
        "/adminPanel/primeIndianVpnConfig",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updatePrimeIndiaVpnConfig(
    data: UpdatePrimeIndiaVpnConfigRequest,
  ): Promise<PrimeIndiaVpnConfigResponse> {
    try {
      const response = await this.api.patch<PrimeIndiaVpnConfigResponse>(
        "/adminPanel/primeIndianVpnConfig",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async cadInternalTransfer(
    data: CadInternalTransferRequest,
  ): Promise<CadInternalTransferResponse> {
    try {
      const response = await this.api.post<CadInternalTransferResponse>(
        "/adminPanel/cad/internal-transfer",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async cadManualPayout(
    data: CadManualPayoutRequest,
  ): Promise<CadManualPayoutResponse> {
    try {
      const response = await this.api.post<CadManualPayoutResponse>(
        "/adminPanel/cad/manual-payout",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async cadOpsClear(data: OpsClearRequest): Promise<OpsClearResponse> {
    try {
      const response = await this.api.post<OpsClearResponse>(
        "/adminPanel/cad/ops-clear",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async ivyOpsClear(data: OpsClearRequest): Promise<OpsClearResponse> {
    try {
      const response = await this.api.post<OpsClearResponse>(
        "/adminPanel/ivy/ops-clear",
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async cadExpireInstance(
    instanceId: string,
  ): Promise<CadIvyActionResponse> {
    try {
      const response = await this.api.post<CadIvyActionResponse>(
        `/adminPanel/cad/expire/${instanceId}`,
        {},
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async cadPayinRefund(
    instanceId: string,
  ): Promise<CadIvyActionResponse> {
    try {
      const response = await this.api.post<CadIvyActionResponse>(
        `/adminPanel/cad/payin/${instanceId}/refund`,
        {},
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async cadHoldReinitiate(
    instanceId: string,
    data: ReinitiateRequest = {},
  ): Promise<CadIvyActionResponse> {
    try {
      const response = await this.api.post<CadIvyActionResponse>(
        `/adminPanel/cad/hold/${instanceId}/reinitiate`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async ivyHoldRefund(
    instanceId: string,
  ): Promise<CadIvyActionResponse> {
    try {
      const response = await this.api.post<CadIvyActionResponse>(
        `/adminPanel/ivy/hold/${instanceId}/refund`,
        {},
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async ivyHoldReinitiate(
    instanceId: string,
    data: ReinitiateRequest = {},
  ): Promise<CadIvyActionResponse> {
    try {
      const response = await this.api.post<CadIvyActionResponse>(
        `/adminPanel/ivy/hold/${instanceId}/reinitiate`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const primeServices = new PrimeApiService();
